import type * as React from 'react';
import type {
  DropdownProps,
  DropdownOption,
  DropdownListVariant,
  DropdownTagsOverflow,
  DropdownSearchMultiCheckboxConfig,
} from '@/shared/types/dropdown';
import type { ButtonTooltipConfig } from '@/shared/types/buttonConfig';
import type {
  Colors,
  IconFontColor,
  InputSize,
  PillSize,
  VisualSlotType,
} from '@/shared/types/types';
import type { ListItemCheckboxSelectionTone } from '@/shared/types/list';

/**
 * Props de `Dropdown` que no deben colarse en `{...spread}` hacia `Input` (ni atributos controlados por el componente).
 */
export const DROPDOWN_NON_INPUT_SPREAD_KEYS = new Set<string>([
  'field',
  'tooltip',
  'slots',
  'items',
  'values',
  'menuSearch',
  'multi',
  'popover',
  'checkbox',
  'list',
  'events',
  'status',
  'loading',
  'label',
  'error',
  'helperText',
  'infoTooltip',
  'size',
  'color',
  'leftSlot',
  'rightSlot',
  'searchPlaceholder',
  'noResultsText',
  'noResultsDescription',
  'caret',
  'value',
  'defaultValue',
  'disabled',
  'placeholder',
  'options',
  'valueName',
  'idName',
  'onValueChange',
  'onOptionSelect',
  'onCheckboxChange',
  'searchable',
  'maxHeight',
  'closeOnClickOutside',
  'dropdownId',
  'fullWidth',
  'auto',
  'multiple',
  'maxSelectedItems',
  'selectionSummary',
  'selectedItemsDisplay',
  'tagsOverflow',
  'tagsOverflowPopoverPlacement',
  'tagsOverflowPopoverZIndex',
  'tagsOverflowPopoverTitle',
  'preventDeselectFromOptionsList',
  'showTriggerClearButton',
  'absolute',
  'popoverZIndex',
  'footer',
  'defaultOptionSelected',
  'withCheckbox',
  'checkboxSelectionTone',
  'checkboxPosition',
  'checkboxListDensity',
  'hasCurrent',
  'onHandleAccept',
  'pill',
  'listVariant',
  'searchMultiCheckbox',
  'searchMultiCheckboxHeader',
  'searchMultiCheckboxShowSearch',
  'searchMultiCheckboxShowClearSelection',
  'onToggleChange',
  'filtersTitle',
  'tooltipProps',
]);

export type MergedDropdownProps = {
  label: string;
  error: string;
  helperText?: string;
  infoTooltip?: string;
  size: InputSize;
  color?: Colors;
  leftSlot: VisualSlotType | null;
  rightSlot: VisualSlotType | null;
  searchPlaceholder: string;
  noResultsText: string;
  noResultsDescription: string;
  caret: boolean;
  value?: string | number | (string | number)[];
  defaultValue: string | number | (string | number)[];
  disabled: boolean;
  placeholder: string;
  options: DropdownOption[];
  valueName: string;
  idName: string;
  onValueChange?: (value: string | number | (string | number)[]) => void;
  onOptionSelect?: (option: DropdownOption) => void;
  onCheckboxChange?: (option: DropdownOption, checked: boolean) => void;
  searchable: boolean;
  maxHeight: string;
  closeOnClickOutside: boolean;
  dropdownId?: string;
  multiple: boolean;
  maxSelectedItems?: number;
  selectionSummary: 'tags' | 'count' | 'text';
  tagsOverflow: DropdownTagsOverflow;
  tagsOverflowPopoverPlacement: 'top' | 'bottom';
  tagsOverflowPopoverZIndex?: number;
  tagsOverflowPopoverTitle?: string;
  preventDeselectFromOptionsList: boolean;
  showTriggerClearButton: boolean;
  absolute: boolean;
  popoverZIndex?: number;
  footer: boolean;
  defaultOptionSelected: boolean;
  withCheckbox: boolean;
  checkboxSelectionTone: ListItemCheckboxSelectionTone;
  checkboxPosition: 'start' | 'end';
  checkboxListDensity: 'default' | 'compact';
  hasCurrent: boolean;
  onHandleAccept?: (selectedOptions: DropdownOption[]) => void;
  pill: PillSize;
  listVariant: DropdownListVariant;
  searchMultiCheckbox?: DropdownSearchMultiCheckboxConfig;
  searchMultiCheckboxHeader?: { id: string; label: string };
  searchMultiCheckboxShowSearch: boolean;
  searchMultiCheckboxShowClearSelection: boolean;
  onToggleChange?: (option: DropdownOption, checked: boolean) => void;
  filtersTitle?: string;
  tooltipProps?: ButtonTooltipConfig;
  loading: boolean;
  fullWidth: boolean;
  auto?: boolean;
  /** `list.userHideAvatars` — variante `user` sin columna de imagen por defecto. */
  userHideAvatars: boolean;
  /** `list.pinSelectedFirst` — selección arriba en el panel (salvo listas con grupos). */
  pinSelectedFirst: boolean;
};

export function mergeGroupedDropdownProps(props: DropdownProps): MergedDropdownProps {
  const field = props.field;
  const tooltip = props.tooltip;
  const slots = props.slots;
  const items = props.items;
  const values = props.values;
  const menuSearch = props.menuSearch;
  const multi = props.multi;
  const popover = props.popover;
  const checkbox = props.checkbox;
  const list = props.list;
  const events = props.events;
  const status = props.status;

  const listVariant = list?.listVariant ?? props.listVariant ?? 'default';
  const searchMultiCheckbox = list?.searchMultiCheckbox ?? props.searchMultiCheckbox;

  return {
    label: field?.label ?? props.label ?? '',
    error: field?.error ?? props.error ?? '',
    helperText: field?.helperText ?? props.helperText,
    infoTooltip: tooltip?.infoTooltip ?? props.infoTooltip,
    size: field?.size ?? props.size ?? 'sm',
    color: field?.color ?? props.color,
    leftSlot: slots?.leftSlot ?? props.leftSlot ?? null,
    rightSlot: slots?.rightSlot ?? props.rightSlot ?? null,
    searchPlaceholder: menuSearch?.searchPlaceholder ?? props.searchPlaceholder ?? 'Buscar...',
    noResultsText: menuSearch?.noResultsText ?? props.noResultsText ?? 'No hay resultados para',
    noResultsDescription:
      menuSearch?.noResultsDescription ??
      props.noResultsDescription ??
      'Puede probar a utilizar otras palabras clave o ajustar los filtros.',
    caret: field?.caret ?? props.caret ?? true,
    value: values?.value ?? props.value,
    defaultValue: values?.defaultValue ?? props.defaultValue ?? '',
    disabled: field?.disabled ?? props.disabled ?? false,
    placeholder: field?.placeholder ?? props.placeholder ?? '',
    options: items?.options ?? props.options ?? [],
    valueName: items?.valueName ?? props.valueName ?? 'label',
    idName: items?.idName ?? props.idName ?? 'value',
    onValueChange: events?.onValueChange ?? props.onValueChange,
    onOptionSelect: events?.onOptionSelect ?? props.onOptionSelect,
    onCheckboxChange: events?.onCheckboxChange ?? props.onCheckboxChange,
    searchable: menuSearch?.searchable ?? props.searchable ?? false,
    maxHeight: popover?.maxHeight ?? props.maxHeight ?? '200px',
    closeOnClickOutside: popover?.closeOnClickOutside ?? props.closeOnClickOutside ?? true,
    dropdownId: popover?.dropdownId ?? props.dropdownId,
    fullWidth: popover?.fullWidth ?? props.fullWidth ?? true,
    auto: popover?.auto ?? props.auto,
    multiple: multi?.multiple ?? props.multiple ?? false,
    maxSelectedItems: multi?.maxSelectedItems ?? props.maxSelectedItems,
    selectionSummary:
      multi?.selectionSummary ??
      multi?.selectedItemsDisplay ??
      props.selectionSummary ??
      props.selectedItemsDisplay ??
      'tags',
    tagsOverflow: multi?.tagsOverflow ?? props.tagsOverflow ?? 'wrap',
    tagsOverflowPopoverPlacement:
      multi?.tagsOverflowPopoverPlacement ?? props.tagsOverflowPopoverPlacement ?? 'bottom',
    tagsOverflowPopoverZIndex: multi?.tagsOverflowPopoverZIndex ?? props.tagsOverflowPopoverZIndex,
    tagsOverflowPopoverTitle: multi?.tagsOverflowPopoverTitle ?? props.tagsOverflowPopoverTitle,
    preventDeselectFromOptionsList:
      multi?.preventDeselectFromOptionsList ?? props.preventDeselectFromOptionsList ?? false,
    showTriggerClearButton: multi?.showTriggerClearButton ?? props.showTriggerClearButton ?? true,
    absolute: popover?.absolute ?? props.absolute ?? false,
    popoverZIndex: popover?.popoverZIndex ?? props.popoverZIndex,
    footer: popover?.footer ?? props.footer ?? false,
    defaultOptionSelected: items?.defaultOptionSelected ?? props.defaultOptionSelected ?? false,
    withCheckbox: checkbox?.withCheckbox ?? props.withCheckbox ?? false,
    checkboxSelectionTone:
      checkbox?.checkboxSelectionTone ?? props.checkboxSelectionTone ?? 'brand',
    checkboxPosition: checkbox?.checkboxPosition ?? props.checkboxPosition ?? 'start',
    checkboxListDensity: checkbox?.checkboxListDensity ?? props.checkboxListDensity ?? 'default',
    hasCurrent: values?.hasCurrent ?? props.hasCurrent ?? true,
    onHandleAccept: events?.onHandleAccept ?? props.onHandleAccept,
    pill: slots?.pill ?? props.pill ?? 'sm',
    listVariant,
    searchMultiCheckbox,
    searchMultiCheckboxHeader: props.searchMultiCheckboxHeader,
    searchMultiCheckboxShowSearch: props.searchMultiCheckboxShowSearch ?? true,
    searchMultiCheckboxShowClearSelection: props.searchMultiCheckboxShowClearSelection ?? true,
    onToggleChange: events?.onToggleChange ?? props.onToggleChange,
    filtersTitle: list?.filtersTitle ?? props.filtersTitle,
    tooltipProps: tooltip?.tooltipProps ?? props.tooltipProps,
    loading: status?.loading ?? props.loading ?? false,
    userHideAvatars: list?.userHideAvatars ?? false,
    pinSelectedFirst: list?.pinSelectedFirst ?? true,
  };
}

export function pickInputPassthroughProps(
  props: DropdownProps
): Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'onChange' | 'value' | 'defaultValue'
> {
  const out: Record<string, unknown> = { ...(props as Record<string, unknown>) };
  for (const key of DROPDOWN_NON_INPUT_SPREAD_KEYS) {
    delete out[key];
  }
  return out as Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'size' | 'onChange' | 'value' | 'defaultValue'
  >;
}
